import { Box, Paper, Typography, Stack, IconButton } from '@mui/material';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { useState, useMemo } from 'react';

/**
 * Minimal month calendar. Consumers pass `events = [{ id, title, startsAt, endsAt }]`
 * and receive `onSelectDay(date)` / `onSelectEvent(event)` callbacks.
 * @param {Date|null} [selectedDay] — jour mis en avant (sélection courante)
 */
function startOfMonth(d) { const x = new Date(d); x.setDate(1); x.setHours(0, 0, 0, 0); return x; }
function startOfWeek(d) {
  const x = new Date(d);
  const delta = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - delta);
  x.setHours(0, 0, 0, 0);
  return x;
}
function addMonths(d, n) { const x = new Date(d); x.setMonth(x.getMonth() + n); return x; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function sameDay(a, b) { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); }
const FR_MONTH = ['Janv.', 'Févr.', 'Mars', 'Avr.', 'Mai', 'Juin', 'Juil.', 'Août', 'Sept.', 'Oct.', 'Nov.', 'Déc.'];
const FR_DAY   = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

export default function Calendar({ events = [], onSelectDay, onSelectEvent, mode = 'month', selectedDay = null }) {
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));

  const cells = useMemo(() => {
    if (mode === 'week') {
      const first = startOfWeek(cursor);
      return Array.from({ length: 7 }).map((_, i) => addDays(first, i));
    }
    const first = startOfMonth(cursor);
    const dayOfWeek = (first.getDay() + 6) % 7; // Monday = 0
    const startDate = new Date(first);
    startDate.setDate(first.getDate() - dayOfWeek);
    const out = [];
    for (let i = 0; i < 42; i += 1) {
      const d = new Date(startDate);
      d.setDate(startDate.getDate() + i);
      out.push(d);
    }
    return out;
  }, [cursor, mode]);

  const eventsOn = (day) =>
    events.filter((e) => sameDay(new Date(e.startsAt), day));

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <IconButton onClick={() => setCursor(mode === 'week' ? addDays(cursor, -7) : addMonths(cursor, -1))}><ChevronLeft /></IconButton>
        <Typography variant="h6">{FR_MONTH[cursor.getMonth()]} {cursor.getFullYear()}</Typography>
        <IconButton onClick={() => setCursor(mode === 'week' ? addDays(cursor, 7) : addMonths(cursor, 1))}><ChevronRight /></IconButton>
      </Stack>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 1 }}>
        {FR_DAY.map((d) => (
          <Typography key={d} variant="caption" sx={{ textAlign: 'center', color: 'text.secondary' }}>{d}</Typography>
        ))}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
        {cells.map((d) => {
          const inMonth = mode === 'week' || d.getMonth() === cursor.getMonth();
          const dayEvents = eventsOn(d);
          const hasEvents = dayEvents.length > 0;
          const isSelected = selectedDay && sameDay(d, selectedDay);
          const isToday = sameDay(d, new Date());
          return (
            <Paper
              key={d.toISOString()}
              onClick={() => onSelectDay?.(d)}
              sx={{
                p: 0.5,
                minHeight: 80,
                cursor: 'pointer',
                opacity: inMonth ? 1 : 0.35,
                position: 'relative',
                bgcolor: isToday ? 'action.selected' : 'background.paper',
                borderWidth: isSelected ? 2 : 1,
                borderStyle: 'solid',
                borderColor: isSelected ? 'primary.main' : 'divider',
                transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
                '&:hover': { boxShadow: 2 },
              }}
            >
              <Typography variant="caption" fontWeight={600}>{d.getDate()}</Typography>
              {hasEvents && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: isSelected ? 'success.dark' : 'success.main',
                  }}
                  aria-hidden
                />
              )}
              {dayEvents.slice(0, 3).map((e) => (
                <Box
                  key={e.id}
                  onClick={(ev) => { ev.stopPropagation(); onSelectEvent?.(e); }}
                  sx={{
                    mt: 0.5, px: 0.5, py: 0.25, borderRadius: 0.5,
                    bgcolor: 'primary.main', color: 'primary.contrastText',
                    fontSize: 11, lineHeight: 1.2, cursor: 'pointer',
                    overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                  }}
                >
                  {e.title}
                </Box>
              ))}
              {dayEvents.length > 3 && (
                <Typography variant="caption" color="text.secondary">+{dayEvents.length - 3}</Typography>
              )}
            </Paper>
          );
        })}
      </Box>
    </Paper>
  );
}
